import * as React from "react";
import { useFormContext, Controller } from "react-hook-form";
import type { CategoryFormValues } from "@/schemas/request-gallery.schemas";
import { Field, FieldContent, FieldError, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGroups } from "@/hooks/useGroups";

const GroupSelector: React.FC<{
  selectedGroups: string[];
  onToggle: (groupId: string) => void;
}> = ({ selectedGroups, onToggle }) => {
  const { data: groupsData, isLoading } = useGroups();

  return (
    <div className="space-y-2">
      <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
        Search Group to share
      </FieldLabel>
      <Input placeholder="Enter group name" className="h-8 text-xs" />
      <ScrollArea className="border rounded-md h-24">
        <div className="p-2 flex flex-wrap gap-2">
          {isLoading ? (
            <p className="text-sm text-neutral-400">Loading groups...</p>
          ) : (
            groupsData?.data.map((group) => {
              const isSelected = selectedGroups.includes(group.id);
              return (
                <Badge
                  key={group.id}
                  variant="outline"
                  onClick={() => onToggle(group.id)}
                  className={cn(
                    "rounded-md text-neutral-500 cursor-pointer",
                    Boolean(isSelected) && "bg-primary-300 text-neutral-white border-primary-300"
                  )}
                >
                  {Boolean(isSelected) && <Check />}
                  {group.name}
                </Badge>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export const RequestCategoryForm = () => {
  const { control, watch, setValue } = useFormContext<CategoryFormValues>();
  const selectedGroups = watch("groups");

  const toggleGroup = (groupId: string) => {
    const currentGroups = selectedGroups ?? [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter((id: string) => id !== groupId)
      : [...currentGroups, groupId];
    setValue("groups", newGroups, { shouldDirty: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className="gap-1">
            <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
              Category Name
            </FieldLabel>
            <FieldContent>
              <Input {...field} placeholder="Category Name" className="h-8 text-xs" />
              <FieldError errors={[fieldState.error]} />
            </FieldContent>
          </Field>
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className="gap-1">
            <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
              Description
            </FieldLabel>
            <FieldContent>
              <Textarea {...field} placeholder="Description" className="text-xs min-h-[60px]" />
              <FieldError errors={[fieldState.error]} />
            </FieldContent>
          </Field>
        )}
      />
      <GroupSelector selectedGroups={selectedGroups} onToggle={toggleGroup} />
    </div>
  );
};
